import { useState, useEffect, useRef, useCallback } from 'react';
import {
  X, Link as LinkIcon, Play, Trash2, ChevronDown, ChevronUp,
  Search, Filter, RefreshCw, Clock, CheckCircle,
  AlertCircle, Info, AlertTriangle, Wifi, WifiOff, Copy, Check,
  ShieldCheck, FilePlus,
} from 'lucide-react';
import { vanyavpnLinksAPI, filesAPI, groupsAPI, vanyavpnAccountsAPI } from '../api';
import './AutoTaskLogs.css';
import './VanyaVpnLinks.css';

const LEVEL_CONFIG = {
  info:    { label: 'INFO',    icon: Info,          cls: 'level-info' },
  success: { label: 'OK',      icon: CheckCircle,   cls: 'level-success' },
  warn:    { label: 'WARN',    icon: AlertTriangle,  cls: 'level-warn' },
  error:   { label: 'ERROR',   icon: AlertCircle,   cls: 'level-error' },
};

const EVENT_LABELS = {
  job_start:         'Запуск',
  job_end:           'Завершение',
  job_skip:          'Пропуск',
  job_error:         'Ошибка',
  job_info:          'Инфо',
  location_start:    'Локация',
  location_ok:       'Ссылка получена',
  location_error:    'Ошибка локации',
  verify_start:      'Запуск проверки',
  verify_end:        'Завершение проверки',
  verify_skip:       'Пропуск проверки',
  verify_error:      'Ошибка проверки',
  verify_aborted:    'Проверка прервана',
  verify_check_start:'Проверка IP',
  verify_open:       'IP открыт',
  verify_closed:     'IP закрыт',
  verify_check_error:'Ошибка проверки IP',
  verify_parse_error:'Ошибка разбора ссылки',
};

const PROTOCOL_OPTIONS = [
  { value: 'ss', label: 'Shadowsocks' },
  { value: 'vless', label: 'VLESS' },
];

// Старые сохранённые результаты содержат только ssLink
function linkOf(item, protocol) {
  if (!item) return null;
  return item.links?.[protocol] || (protocol === 'ss' ? item.ssLink : null) || null;
}

function formatTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}

function LogEntry({ entry }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = LEVEL_CONFIG[entry.level] || LEVEL_CONFIG.info;
  const Icon = cfg.icon;
  const hasDetails = entry.details && Object.keys(entry.details).length > 0;

  return (
    <div className={`log-entry ${cfg.cls}`}>
      <div className="log-entry-main" onClick={() => hasDetails && setExpanded(v => !v)}>
        <div className="log-entry-left">
          <Icon size={13} className="log-icon" />
          <span className="log-time">{formatTime(entry.timestamp)}</span>
          <span className={`log-badge ${cfg.cls}`}>{cfg.label}</span>
          <span className="log-event">{EVENT_LABELS[entry.event] || entry.event}</span>
        </div>
        <div className="log-entry-right">
          <span className="log-message">{entry.message}</span>
          {hasDetails && (
            <button className="log-expand-btn" title="Подробности">
              {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            </button>
          )}
        </div>
      </div>
      {expanded && hasDetails && (
        <div className="log-details">
          {Object.entries(entry.details).map(([k, v]) => (
            <div key={k} className="log-detail-row">
              <span className="log-detail-key">{k}:</span>
              <span className="log-detail-val">
                {typeof v === 'object' ? JSON.stringify(v) : String(v)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ResultRow({ item, protocol }) {
  const [copied, setCopied] = useState(false);
  const link = linkOf(item, protocol);
  const handleCopy = () => {
    if (!link) return;
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="vl-row">
      <span className="vl-row-country">{item.country}</span>
      <span className="vl-row-link">{link || '— нет ссылки для этого протокола —'}</span>
      <button className="vl-row-copy" onClick={handleCopy} title="Скопировать ссылку" disabled={!link}>
        {copied ? <Check size={12} /> : <Copy size={12} />}
      </button>
    </div>
  );
}

function VerifiedRow({ item, protocol }) {
  const [copied, setCopied] = useState(false);
  const link = item.link || linkOf(item, protocol);
  const handleCopy = () => {
    if (!link) return;
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="vl-row">
      <span className="vl-row-flag">{item.flag}</span>
      <span className="vl-row-country">{item.country}</span>
      <span className="vl-row-link">{link}</span>
      <button className="vl-row-copy" onClick={handleCopy} title="Скопировать ссылку" disabled={!link}>
        {copied ? <Check size={12} /> : <Copy size={12} />}
      </button>
    </div>
  );
}

function VanyaVpnLinks({ onClose }) {
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState(null);
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [filterLevel, setFilterLevel] = useState('all');
  const [search, setSearch] = useState('');
  const [newestFirst, setNewestFirst] = useState(true);
  const [sseConnected, setSseConnected] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);
  const [verifiedResults, setVerifiedResults] = useState([]);
  const [showVerified, setShowVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [copiedAllVerified, setCopiedAllVerified] = useState(false);
  const [isAppending, setIsAppending] = useState(false);
  const [appendMessage, setAppendMessage] = useState('');
  const [appendProgress, setAppendProgress] = useState({ current: 0, total: 0 });
  const [groups, setGroups] = useState({});
  const [selectedGroups, setSelectedGroups] = useState(new Set());
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [showNewAccountForm, setShowNewAccountForm] = useState(false);
  const [newAccountLabel, setNewAccountLabel] = useState('');
  const [newAccountJwt, setNewAccountJwt] = useState('');
  const [runError, setRunError] = useState('');
  const [verifyDoneMessage, setVerifyDoneMessage] = useState('');
  const [protocol, setProtocol] = useState('ss');

  const logsEndRef = useRef(null);
  const logsTopRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const esRef = useRef(null);
  const selectedAccountIdRef = useRef('');
  selectedAccountIdRef.current = selectedAccountId;
  const protocolRef = useRef('ss');
  protocolRef.current = protocol;

  const loadResults = useCallback(async (accountId) => {
    if (!accountId) { setResults([]); return; }
    try {
      const res = await vanyavpnLinksAPI.getResults(accountId);
      if (!Array.isArray(res.data)) return;
      setResults(res.data);
      setShowResults(res.data.length > 0);
    } catch {}
  }, []);

  const loadVerified = useCallback(async (accountId, proto = protocolRef.current) => {
    if (!accountId) { setVerifiedResults([]); return; }
    try {
      const res = await vanyavpnLinksAPI.getVerified(accountId, proto);
      if (!Array.isArray(res.data)) return;
      setVerifiedResults(res.data);
      setShowVerified(res.data.length > 0);
    } catch {}
  }, []);

  const loadLogs = useCallback(async (accountId) => {
    try {
      const [logsRes, statusRes] = await Promise.all([
        vanyavpnLinksAPI.getLogs(),
        vanyavpnLinksAPI.getStatus(accountId, protocolRef.current),
      ]);
      if (Array.isArray(logsRes.data)) setLogs(logsRes.data);
      if (statusRes.data && typeof statusRes.data === 'object') setStatus(statusRes.data);
      // грузим всегда: при пустом счётчике выбранного протокола в стейте
      // иначе остались бы результаты прошлого сбора
      loadResults(accountId);
      loadVerified(accountId);
    } catch {}
  }, [loadResults, loadVerified]);

  // После завершения сбора/проверки перечитываем всё сразу — иначе статус-бар
  // висит с цифрами, загруженными до запуска.
  const refreshAll = useCallback(async () => {
    const accountId = selectedAccountIdRef.current;
    if (!accountId) return;
    const proto = protocolRef.current;
    loadResults(accountId);
    loadVerified(accountId, proto);
    try {
      const res = await vanyavpnLinksAPI.getStatus(accountId, proto);
      if (res.data && typeof res.data === 'object') setStatus(res.data);
    } catch {}
  }, [loadResults, loadVerified]);

  useEffect(() => {
    const token = localStorage.getItem('jwt_token');
    if (!token) return;

    const es = new EventSource(`/vanyavpn-links/logs/stream?token=${encodeURIComponent(token)}`);
    esRef.current = es;

    es.onopen = () => setSseConnected(true);
    es.onerror = () => setSseConnected(false);

    es.onmessage = (e) => {
      try {
        const entry = JSON.parse(e.data);
        setLogs(prev => [...prev, entry]);
        if (entry.event === 'job_start') { setIsRunning(true); setRunError(''); }
        if (entry.event === 'job_end') {
          setIsRunning(false);
          refreshAll();
        }
        if (entry.event === 'job_error' || entry.event === 'job_skip') {
          setIsRunning(false);
          setRunError(entry.message);
        }
        if (entry.event === 'verify_start') { setIsVerifying(true); setVerifyDoneMessage(''); }
        if (entry.event === 'verify_end') {
          setIsVerifying(false);
          refreshAll();
          setVerifyDoneMessage(entry.message);
          setTimeout(() => setVerifyDoneMessage(''), 6000);
        }
        if (entry.event === 'verify_error' || entry.event === 'verify_skip') {
          setIsVerifying(false);
          refreshAll();
        }
      } catch {}
    };

    loadLogs(selectedAccountIdRef.current);

    return () => { es.close(); esRef.current = null; };
  }, [loadLogs, refreshAll]);

  useEffect(() => {
    if (!selectedAccountId) { setResults([]); setVerifiedResults([]); return; }
    loadResults(selectedAccountId);
    loadVerified(selectedAccountId, protocol);
    vanyavpnLinksAPI.getStatus(selectedAccountId, protocol).then(res => {
      if (res.data && typeof res.data === 'object') setStatus(res.data);
    }).catch(() => {});
  }, [selectedAccountId, protocol, loadResults, loadVerified]);

  useEffect(() => {
    groupsAPI.getAll().then(res => {
      if (res.data?.groups) setGroups(res.data.groups);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    vanyavpnAccountsAPI.getAll().then(res => {
      if (Array.isArray(res.data)) {
        setAccounts(res.data);
        if (res.data.length > 0) setSelectedAccountId(res.data[0].id);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!autoScroll) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    if (newestFirst) {
      el.scrollTop = 0;
    } else {
      el.scrollTop = el.scrollHeight;
    }
  }, [logs, newestFirst, autoScroll]);

  const handleScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    if (newestFirst) {
      setAutoScroll(el.scrollTop < 80);
    } else {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
      setAutoScroll(atBottom);
    }
  };

  const handleRun = async () => {
    setRunError('');
    if (!selectedAccountId) { setRunError('Сначала выберите аккаунт'); return; }
    if (jobRunning || verifying) { setRunError('Дождитесь завершения текущей операции'); return; }
    try {
      await vanyavpnLinksAPI.run(selectedAccountId);
      setIsRunning(true);
      setShowResults(false);
    } catch (error) {
      setRunError('Не удалось запустить сбор: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleSaveNewAccount = async () => {
    if (!newAccountLabel.trim() || !newAccountJwt.trim()) return;
    try {
      const res = await vanyavpnAccountsAPI.create(newAccountLabel.trim(), newAccountJwt.trim());
      setAccounts(prev => [...prev, res.data]);
      setSelectedAccountId(res.data.id);
      setShowNewAccountForm(false);
      setNewAccountLabel('');
      setNewAccountJwt('');
    } catch {}
  };

  const handleDeleteAccount = async () => {
    if (!selectedAccountId) return;
    if (!confirm('Удалить выбранный аккаунт?')) return;
    try {
      await vanyavpnAccountsAPI.delete(selectedAccountId);
      setAccounts(prev => prev.filter(a => a.id !== selectedAccountId));
      setSelectedAccountId('');
    } catch {}
  };

  const handleClear = async () => {
    if (!confirm('Очистить все логи?')) return;
    try {
      await vanyavpnLinksAPI.clearLogs();
      setLogs([]);
    } catch {}
  };

  const handleCopyAll = () => {
    const text = results
      .filter(r => linkOf(r, protocol))
      .map(r => `${r.country}: ${linkOf(r, protocol)}`).join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 1500);
    });
  };

  const handleVerify = async () => {
    if (!selectedAccountId) return;
    try {
      await vanyavpnLinksAPI.verify(selectedAccountId, protocol);
      setIsVerifying(true);
      setShowVerified(false);
    } catch {}
  };

  const handleCopyAllVerified = () => {
    const text = verifiedResults.map(r => `${r.country}: ${r.link || linkOf(r, protocol)}`).join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopiedAllVerified(true);
      setTimeout(() => setCopiedAllVerified(false), 1500);
    });
  };

  const toggleGroup = (gid) => {
    setSelectedGroups(prev => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid); else next.add(gid);
      return next;
    });
  };

  const handleAppendToSelectedGroups = async () => {
    if (verifiedResults.length === 0 || selectedGroups.size === 0) return;
    if (!confirm(`Заменить содержимое файлов в выбранных группах (${selectedGroups.size}) на ${verifiedResults.length} проверенных ссылок? Старые ссылки будут удалены.`)) return;

    setIsAppending(true);
    setAppendMessage('');

    try {
      const { data } = await filesAPI.getAll();
      const allFiles = data.files || [];
      const targetFiles = allFiles.filter(f => selectedGroups.has(f.group || '__ungrouped'));
      setAppendProgress({ current: 0, total: targetFiles.length });

      if (targetFiles.length === 0) {
        setAppendMessage('В выбранных группах нет файлов');
        return;
      }

      const newContent = verifiedResults
        .map(r => r.link || linkOf(r, protocol))
        .filter(Boolean)
        .join('\n');
      let successCount = 0, errorCount = 0;
      const errors = [];

      for (const file of targetFiles) {
        try {
          await filesAPI.update(file.name, newContent);
          successCount++;
        } catch (error) {
          errorCount++;
          errors.push({ name: file.name, error: error.response?.data?.error || error.message });
        }
        setAppendProgress(p => ({ ...p, current: p.current + 1 }));
      }

      setAppendMessage(
        errorCount === 0
          ? `✅ Заменено в ${successCount} файлах`
          : `✅ Успешно: ${successCount}\n❌ Ошибок: ${errorCount}\n${errors.map(e => `• ${e.name}: ${e.error}`).join('\n')}`
      );
    } catch {
      setAppendMessage('Ошибка загрузки списка файлов');
    } finally {
      setIsAppending(false);
    }
  };

  const filteredLogs = logs.filter(entry => {
    if (filterLevel !== 'all' && entry.level !== filterLevel) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        entry.message.toLowerCase().includes(q) ||
        entry.event.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const displayedLogs = newestFirst ? [...filteredLogs].reverse() : filteredLogs;
  const jobRunning = isRunning || status?.running;
  const verifying = isVerifying || status?.verifying;
  const protoLabel = PROTOCOL_OPTIONS.find(p => p.value === protocol)?.label || protocol;
  const protoResults = results.filter(r => linkOf(r, protocol));

  return (
    <div className="at-overlay" onClick={onClose}>
      <div className="at-panel" onClick={e => e.stopPropagation()}>

        <div className="at-header">
          <div className="at-header-left">
            <LinkIcon size={16} className="at-header-icon" />
            <span className="at-title">Ссылки vpn.com</span>
            <div className={`at-status-badge ${jobRunning ? 'running' : 'idle'}`}>
              {jobRunning ? (
                <><RefreshCw size={10} className="spin" /> Выполняется</>
              ) : (
                <><Clock size={10} /> Ожидание</>
              )}
            </div>
            <div className={`at-sse-badge ${sseConnected ? 'connected' : 'disconnected'}`}>
              {sseConnected
                ? <><Wifi size={10} /> Live</>
                : <><WifiOff size={10} /> Offline</>
              }
            </div>
          </div>

          <div className="at-header-right">
            <select
              className="vl-account-select"
              value={selectedAccountId}
              onChange={(e) => {
                if (e.target.value === '__new') { setShowNewAccountForm(true); return; }
                setSelectedAccountId(e.target.value);
              }}
              disabled={jobRunning || verifying}
            >
              <option value="" disabled>— выберите аккаунт —</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
              <option value="__new">+ Добавить аккаунт...</option>
            </select>
            {selectedAccountId && (
              <button className="at-btn at-btn-ghost" onClick={handleDeleteAccount} title="Удалить аккаунт" disabled={jobRunning || verifying}>
                <Trash2 size={12} />
              </button>
            )}
            <select
              className="vl-proto-select"
              value={protocol}
              onChange={(e) => setProtocol(e.target.value)}
              disabled={jobRunning || verifying}
              title="Протокол ссылок"
            >
              {PROTOCOL_OPTIONS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            <button
              className="at-btn at-btn-run"
              onClick={handleRun}
              disabled={jobRunning || verifying || !selectedAccountId}
              title="Собрать ссылки обоих протоколов (Shadowsocks + VLESS)"
            >
              <Play size={13} /> Получить все ссылки
            </button>
            <button
              className="at-btn at-btn-run"
              onClick={handleVerify}
              disabled={jobRunning || verifying || protoResults.length === 0}
              title={status?.probeOnline === false ? 'ProbeApp offline — проверка может начаться с задержкой' : 'Проверить IP через ProbeApp'}
            >
              <ShieldCheck size={13} /> Проверить через ProbeApp
            </button>
            <button className="at-btn at-btn-ghost" onClick={handleClear} title="Очистить логи">
              <Trash2 size={13} />
            </button>
            <button className="at-btn at-btn-ghost" onClick={onClose} title="Закрыть">
              <X size={15} />
            </button>
          </div>
        </div>

        {runError && <div className="vl-run-error">{runError}</div>}
        {verifyDoneMessage && <div className="vl-verify-done">✅ {verifyDoneMessage}</div>}

        {showNewAccountForm && (
          <div className="vl-new-account-form">
            <input
              placeholder="Название аккаунта"
              value={newAccountLabel}
              onChange={e => setNewAccountLabel(e.target.value)}
            />
            <input
              placeholder="JWT токен"
              value={newAccountJwt}
              onChange={e => setNewAccountJwt(e.target.value)}
            />
            <button className="at-btn at-btn-ghost" onClick={handleSaveNewAccount} title="Сохранить">
              <Check size={12} />
            </button>
            <button className="at-btn at-btn-ghost" onClick={() => setShowNewAccountForm(false)} title="Отмена">
              <X size={12} />
            </button>
          </div>
        )}

        {status && (
          <div className="at-status-bar">
            <span>
              Аккаунт: <b>{accounts.find(a => a.id === selectedAccountId)?.label || '— не выбран —'}</b>
            </span>
            {status.lastRunAt && (
              <span>
                Последний запуск: <b>{formatDate(status.lastRunAt)} {formatTime(status.lastRunAt)}</b>
                {status.lastRunDuration != null && <> ({status.lastRunDuration}с)</>}
              </span>
            )}
            <span>Протокол: <b>{protoLabel}</b></span>
            <span>
              Ссылок собрано: <b>{status.resultCount}</b>
              {status.protoCounts && (
                <> (SS: {status.protoCounts.ss}, VLESS: {status.protoCounts.vless})</>
              )}
            </span>
            <span>Проверено (открыто): <b>{status.verifiedCount}</b></span>
            <span>Логов: <b>{status.logCount}</b></span>
            <span>SSE клиентов: <b>{status.sseClients}</b></span>
            <span className={status.probeOnline ? 'vl-probe-online' : 'vl-probe-offline'}>
              ProbeApp: <b>{status.probeOnline ? 'онлайн' : 'офлайн'}</b>
            </span>
          </div>
        )}

        {results.length > 0 && (
          <div className="vl-results-section">
            <button className="vl-results-toggle" onClick={() => setShowResults(v => !v)}>
              {showResults ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              Результаты — {protoLabel} ({protoResults.length}{protoResults.length !== results.length ? ` из ${results.length}` : ''})
            </button>
            {showResults && (
              <>
                <div className="vl-results-toolbar">
                  <button className="at-btn at-btn-ghost" onClick={handleCopyAll}>
                    {copiedAll ? <Check size={12} /> : <Copy size={12} />} Скопировать всё
                  </button>
                </div>
                <div className="vl-results-list">
                  {protoResults.length === 0 ? (
                    <div className="vl-row"><span className="vl-row-link">Нет {protoLabel}-ссылок — запустите сбор заново</span></div>
                  ) : (
                    protoResults.map(item => <ResultRow key={item.value} item={item} protocol={protocol} />)
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {verifiedResults.length > 0 && (
          <div className="vl-results-section">
            <button className="vl-results-toggle" onClick={() => setShowVerified(v => !v)}>
              {showVerified ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              Проверенные (открытые) — {protoLabel} ({verifiedResults.length})
            </button>
            {showVerified && (
              <>
                <div className="vl-results-toolbar">
                  <button className="at-btn at-btn-ghost" onClick={handleCopyAllVerified}>
                    {copiedAllVerified ? <Check size={12} /> : <Copy size={12} />} Скопировать всё
                  </button>
                </div>
                <div className="vl-group-picker">
                  <div className="vl-group-picker-header">Группы для дозаписи:</div>
                  <div className="vl-group-picker-list">
                    <label className="vl-group-checkbox">
                      <input type="checkbox" checked={selectedGroups.has('__ungrouped')} onChange={() => toggleGroup('__ungrouped')} />
                      Без группы
                    </label>
                    {Object.entries(groups).map(([id, g]) => (
                      <label key={id} className="vl-group-checkbox">
                        <input type="checkbox" checked={selectedGroups.has(id)} onChange={() => toggleGroup(id)} />
                        {g.name}
                      </label>
                    ))}
                  </div>
                  <button
                    className="at-btn at-btn-run"
                    onClick={handleAppendToSelectedGroups}
                    disabled={isAppending || selectedGroups.size === 0}
                  >
                    <FilePlus size={12} /> Заменить в выбранных группах
                  </button>
                </div>
                {appendMessage && <div className="vl-append-message">{appendMessage}</div>}
                {isAppending && (
                  <div className="vl-append-progress">
                    Добавлено в {appendProgress.current}/{appendProgress.total} файлов
                  </div>
                )}
                <div className="vl-results-list">
                  {verifiedResults.map(item => <VerifiedRow key={item.value} item={item} protocol={protocol} />)}
                </div>
              </>
            )}
          </div>
        )}

        <div className="at-toolbar">
          <div className="at-search-box">
            <Search size={12} className="at-search-icon" />
            <input
              className="at-search-input"
              type="text"
              placeholder="Поиск..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button className="at-search-clear" onClick={() => setSearch('')}>
                <X size={11} />
              </button>
            )}
          </div>

          <div className="at-filter-group">
            <Filter size={12} />
            {['all', 'info', 'success', 'warn', 'error'].map(lvl => (
              <button
                key={lvl}
                className={`at-filter-btn ${filterLevel === lvl ? 'active' : ''} ${lvl !== 'all' ? `filter-${lvl}` : ''}`}
                onClick={() => setFilterLevel(lvl)}
              >
                {lvl === 'all' ? 'Все' : LEVEL_CONFIG[lvl]?.label || lvl}
              </button>
            ))}
          </div>

          <button
            className={`at-order-btn ${newestFirst ? 'active' : ''}`}
            onClick={() => setNewestFirst(v => !v)}
            title={newestFirst ? 'Новые сверху' : 'Новые снизу'}
          >
            {newestFirst ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
            {newestFirst ? 'Новые сверху' : 'Новые снизу'}
          </button>

          <span className="at-count">{filteredLogs.length} записей</span>
        </div>

        <div className="at-log-wrap">
          <div
            className="at-log-list"
            ref={scrollContainerRef}
            onScroll={handleScroll}
          >
            <div ref={newestFirst ? logsTopRef : undefined} />
            {displayedLogs.length === 0 ? (
              <div className="at-empty">
                <LinkIcon size={28} />
                <p>Логов нет</p>
                <span>Нажмите «Получить все ссылки»</span>
              </div>
            ) : (
              displayedLogs.map(entry => (
                <LogEntry key={entry.id} entry={entry} />
              ))
            )}
            <div ref={newestFirst ? undefined : logsEndRef} />
          </div>

          {!autoScroll && (
            <button
              className="at-scroll-btn"
              onClick={() => {
                setAutoScroll(true);
                const el = scrollContainerRef.current;
                if (el) el.scrollTop = newestFirst ? 0 : el.scrollHeight;
              }}
            >
              {newestFirst ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              В конец
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default VanyaVpnLinks;
